import { UserSubaccounts } from "@decibeltrade/sdk";
import { type FetchQueryOptions, useQuery, useSuspenseQuery } from "@tanstack/react-query";

import { isValidAptosAddress } from "@/utils/validate";

import { readDexSdk } from "../dex-sdk";

export interface GetUserSubaccountsArgs {
  ownerAddr: string;
}

export function userSubaccountsQueryKey({ ownerAddr }: GetUserSubaccountsArgs) {
  return ["user-subaccounts", ownerAddr] as const;
}

export function userSubaccountsQuery({ ownerAddr }: GetUserSubaccountsArgs) {
  return {
    queryKey: userSubaccountsQueryKey({ ownerAddr }),
    queryFn: async (): Promise<UserSubaccounts> => {
      return await readDexSdk.userSubaccounts.getByAddr({ ownerAddr });
    },
  } satisfies FetchQueryOptions;
}

export function useUserSubaccounts({ ownerAddr }: GetUserSubaccountsArgs) {
  return useSuspenseQuery<UserSubaccounts>(userSubaccountsQuery({ ownerAddr }));
}

export function useUserSubaccountsQuery({ ownerAddr }: { ownerAddr: string | undefined }) {
  return useQuery({
    ...userSubaccountsQuery({
      ownerAddr: ownerAddr ?? "",
    }),
    enabled: !!ownerAddr,
  });
}

export function isSubaccountQueryKey(address: string) {
  return ["is-subaccount", address] as const;
}

export function useIsSubaccountQuery(address: string) {
  return useQuery({
    queryKey: isSubaccountQueryKey(address),
    queryFn: () => readDexSdk.userSubaccounts.isSubaccount(address),
    enabled: address.length > 0 && isValidAptosAddress(address),
    staleTime: 30_000,
  });
}

export function usePrimarySubaccountExistsQuery({ ownerAddr }: { ownerAddr: string | undefined }) {
  const { data: subaccounts = [], isLoading: isLoadingSubaccounts } = useUserSubaccountsQuery({
    ownerAddr: ownerAddr ?? "",
  });

  return {
    exists: subaccounts.some((sub) => sub.is_primary),
    isLoading: isLoadingSubaccounts,
  };
}
